"use client";

import { useRef } from "react";
import { ArrowRight, BookOpen } from "lucide-react";
import { Eyebrow, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger, useMaskedReveal } from "@/components/sections/kit/motion";
import { routePath } from "@/lib/routes";

/** The learn-hub article behind this page's method. Lives in the CMS now (slug `utm-hygiene`);
 *  keep title and read time in sync with the published article. */
const post = {
  slug: "utm-hygiene",
  title: "UTM hygiene: a naming convention your future self will thank you for",
  readTime: "4 min read",
} as const;

/**
 * Proof, the honest kind — no logo wall, no borrowed stats. One receipt:
 * the real learn-hub article behind this page's method. Title and read time
 * are a literal; keep them in sync with the CMS article.
 */
export default function ProofSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  // Head-left / card-right share ONE grid block, so the head is inlined
  // (SectionHead would mark itself and double-clear) with its own reveal.
  useMaskedReveal(headRef, { heading: "h2" });
  useEnterStagger(rootRef, "[data-proof-card]", { y: 24 });

  return (
    <SectionShell id="proof" className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px">
      <div ref={rootRef} className="flex flex-col">
        <div
          data-grid-block
          className="grid items-center gap-10 lg:grid-cols-[5fr_7fr] lg:gap-12"
        >
          <div ref={headRef}>
            <div data-head-sub className="flex">
              <Eyebrow>Proof, the honest kind</Eyebrow>
            </div>
            <h2 className="mt-4 font-onest text-[28px] font-extrabold tracking-[-0.025em]">
              No logo wall. Receipts instead.
            </h2>
            <p
              data-head-sub
              className="mt-2.5 text-[14.5px] leading-[1.6] text-muted [text-wrap:pretty]"
            >
              We&apos;re new, and borrowed stats aren&apos;t ours to claim. What we can show you is
              the method — how attribution actually breaks, and the discipline that fixes it.
            </p>
          </div>

          <a
            href={`${routePath("learn")}/${post.slug}`}
            data-proof-card
            className="group grid grid-cols-[52px_minmax(0,1fr)_24px] items-center gap-[18px] rounded-ref-lg border border-line bg-surface p-7 shadow-ref transition-shadow hover:shadow-ref-lg max-sm:grid-cols-[44px_minmax(0,1fr)] max-sm:p-5"
          >
            <span className="flex h-[52px] w-[52px] items-center justify-center rounded-[13px] border text-brand-600 [background:color-mix(in_srgb,var(--color-brand-600)_7%,white)] [border-color:color-mix(in_srgb,var(--color-brand-600)_18%,transparent)] max-sm:h-11 max-sm:w-11">
              <BookOpen size={22} strokeWidth={1.7} />
            </span>
            <span>
              <span className="block font-jetbrains text-[10px] uppercase tracking-[0.12em] text-dim">
                From the learn hub · {post.readTime}
              </span>
              <span className="mt-[5px] block text-[16.5px] font-bold leading-snug tracking-[-0.01em]">
                {post.title}
              </span>
              <span className="mt-1 block text-[13px] text-muted">
                The naming discipline behind this page — free, no signup.
              </span>
            </span>
            <span className="text-brand-600 transition-transform group-hover:translate-x-0.5 max-sm:hidden">
              <ArrowRight size={18} />
            </span>
          </a>
        </div>
      </div>
    </SectionShell>
  );
}
